import i18n, { ResourceLanguage, TOptions } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import 'intl-pluralrules';

import {
  formatLongDate,
  formatRelativeDate,
  formatShortDate,
  formatTime,
  formatTimeAgo,
  getDayPeriod,
} from './formatters/date';
import en from './languages/en.json';

const resources = {
  en: {
    translation: en as ResourceLanguage,
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  })
  .catch((err) => {
    console.warn('Failed to initialize i18next', err);
  });

// Custom formatters
i18n.services.formatter?.add('shortDate', (value, lng) => formatShortDate(value, lng));
i18n.services.formatter?.add('longDate', (value, lng, options) =>
  formatLongDate(value, lng, options),
);
i18n.services.formatter?.add('relativeDate', (value, lng) => formatRelativeDate(value, lng));
i18n.services.formatter?.add('time', (value, lng) => formatTime(value, lng));
i18n.services.formatter?.add('timeAgo', (value, lng) => formatTimeAgo(value, lng));
i18n.services.formatter?.add('lowercase', (value) => String(value).toLowerCase());

export const useTranslationPrefix = (prefix: string) => {
  const { t } = useTranslation();

  return (key: string, options?: TOptions) => {
    // switchDayPeriod option
    // Get the current day period (morning, afternoon, evening or night) and append it to the key.
    // Example:
    //  {
    //    "greeting": "Hi!"
    //    "greeting_morning": "Good morning!"
    //    "greeting_afternoon": "Good afternoon!"
    //    "greeting_evening": "Good evening!"
    //    "greeting_night": "Good evening!"
    //  }
    // It will fall back to the original key ("greeting" in the example) if the modified key does not exist.
    if (options?.switchDayPeriod) {
      const dayPeriod = getDayPeriod(new Date());
      const dayPeriodKey = `${prefix}.${key}_${dayPeriod}`;

      if (i18n.exists(dayPeriodKey)) {
        return t(dayPeriodKey, options);
      }
    }

    return t(`${prefix}.${key}`, options);
  };
};

export default i18n;
